import React, { Fragment, ReactNode, useEffect, useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { t } from "i18next";
import {
  faCloudArrowDown,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Branch } from "@/app/branchs/branchs.type";
import { Select } from "@/shared/components/organisms/Select";
import { Option } from "@/shared/components/molecules/OptionsSelect";

import { ButtonPrimary } from "@/shared/components/atoms/ButtonPrimary";

type DialogViewProps = {
  children?: React.ReactNode;
  open: boolean;
  setOpen: (open: boolean) => void;
  tittle: string;
  subTittle?: string;
  OnclickCreate: () => void;
  OnclickCancel: () => void;

  branchSelected: Option;
  setBranchSelected: (option: Option) => void;
  branches?: Branch[];

  tittleButtonCreate: string;
  tittleButtonCancel: string;

  hideFooter?: boolean;
  isLoading?: boolean;
  acceptDisabled?: boolean;
  cancelDisabled?: boolean;
  footer?: ReactNode;

  handleExport?: () => void;
  openModal?: () => void;
};

export const DialogView: React.FC<DialogViewProps> = ({
  children,
  open,
  setOpen,
  tittle,
  subTittle,
  tittleButtonCreate,
  tittleButtonCancel,
  OnclickCreate,
  OnclickCancel,
  isLoading,
  acceptDisabled,
  cancelDisabled,
  branchSelected,
  setBranchSelected,
  branches,
  hideFooter,
  footer,
  handleExport,
  openModal,
}) => {
  const [branchesOptions, setBranchesOptions] = useState<Option[]>([]);
  
  useEffect(() => {
    if (branches) {
      setBranchesOptions(
        branches.map((branch: Branch) => ({
          id: branch.id,
          name: branch.branch,
        }))
      );
    }
  }, [branches]);

  return (
    <Fragment>
      <Dialog open={open} onClose={setOpen} className="relative z-10">
        <div className="fixed inset-0" />

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
              <DialogPanel
                transition
                className="pointer-events-auto w-screen max-w-6xl transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
              >
                <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                  <div className="flex-1">
                    {/* Header */}
                    <div className="bg-gray-50 px-4 py-6 sm:px-6">
                      <div className="flex items-start justify-between space-x-3">
                        <div className="space-y-1">
                          <DialogTitle className="text-base font-semibold leading-6 text-gray-900">
                            {tittle}
                          </DialogTitle>
                          <p className="text-sm text-gray-500">{subTittle}</p>
                        </div>

                        <div className="relative">
                          <button
                            type="button"
                            className="text-gray-400 hover:text-gray-500 hidden md:block"
                          >
                            <span className="sr-only">Export</span>
                            <FontAwesomeIcon
                              icon={faCloudArrowDown}
                              aria-hidden="true"
                              className="h-10 w-10"
                              onClick={handleExport}
                            />
                          </button>
                        </div>

                        <ButtonPrimary
                          text={'Definir porcentajes de ocupación para el semáforo'}
                          size="small-xs"
                          onClick={openModal}
                        />

                        <Select
                          name="branch"
                          selected={branchSelected}
                          setSelected={setBranchSelected}
                          options={branchesOptions}
                          tittle={''}
                        />

                        <div className="flex h-7 items-center">
                          <button
                            type="button"
                            onClick={OnclickCancel}
                            className="relative text-gray-400 hover:text-gray-500"
                          >
                            <span className="absolute -inset-2.5" />
                            <span className="sr-only">Close panel</span>
                            <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Divider container */}
                    <div className="space-y-6 py-6 sm:space-y-0 sm:divide-y sm:divide-gray-200 sm:py-0">
                      {children}
                    </div>
                  </div>

                  {/* Action buttons */}
                  {footer ? (
                    <Fragment>
                      <div className="flex-shrink-0 border-t border-gray-200 px-4 py-5 sm:px-6">
                        {footer}
                      </div>
                    </Fragment>
                  ) : (
                    <Fragment>
                      {hideFooter ? (
                        <Fragment />
                      ) : (
                        <div className="flex-shrink-0 border-t border-gray-200 px-4 py-5 sm:px-6">
                          <div className="flex justify-end space-x-3">
                            <button
                              disabled={cancelDisabled}
                              type="button"
                              onClick={OnclickCancel}
                              className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                              {tittleButtonCancel}
                            </button>
                            <button
                              disabled={acceptDisabled}
                              onClick={OnclickCreate}
                              type="button"
                              className="inline-flex gap-2 justify-center items-center rounded-md bg-primary-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
                            >
                              {isLoading && (
                                <div
                                  className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] dark:text-white"
                                  role="status"
                                ></div>
                              )}
                              {tittleButtonCreate}
                            </button>
                          </div>
                        </div>
                      )}
                    </Fragment>
                  )}
                </div>
              </DialogPanel>
            </div>
          </div>
        </div>
      </Dialog>
    </Fragment>
  );
};
